<template>
  <v-container fluid class="capture-accounts">
    <div class="d-flex justify-space-between align-center mb-4">
      <h1 class="form-title">Structure Capture Accounts</h1>
      <v-btn class="str-btn-primary" @click="openCreate" data-testid="add-account-btn">
        + Add Capture Account
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="accounts"
      :loading="loading"
      :search="search"
      item-value="id"
      data-testid="accounts-table"
    >
      <template #top>
        <v-text-field
          v-model="search"
          label="Search by name, organization ID or user"
          prepend-inner-icon="fas fa-search"
          single-line
          hide-details
          variant="outlined"
          density="compact"
          class="ma-2"
        ></v-text-field>
      </template>
      <template #[`item.organization_id`]="{ item }">
        <span class="mono">{{ item.organization_id }}</span>
      </template>
      <template #[`item.auth0_id`]="{ item }">
        {{ item.auth0_id ? (emailFor(item.auth0_id) || item.auth0_id) : '— not linked —' }}
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip :color="statusColour(item.status)" size="small">{{ item.status }}</v-chip>
      </template>
      <template #[`item.active_device_count`]="{ item }">
        <!-- The count alone cannot settle a dispute, so it is the link to the serials. -->
        <a class="seat-link" @click.stop="openUsage(item)" data-testid="seat-count-link">
          <strong>{{ item.active_device_count }}</strong>
          <span class="period"> in {{ periodLabel(item) }}</span>
        </a>
      </template>
      <template #[`item.created_on`]="{ item }">{{ formatDate(item.created_on) }}</template>
      <template #[`item.actions`]="{ item }">
        <v-btn size="small" variant="text" icon aria-label="Edit account" @click="openEdit(item)" data-testid="edit-btn">
          <v-icon>fas fa-pen</v-icon>
        </v-btn>
      </template>
      <template #no-data>No Structure Capture accounts yet.</template>
    </v-data-table>

    <v-dialog v-model="usageDialog" max-width="900" scrollable>
      <v-card v-if="usageAccount">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap">
          <span>{{ usageAccount.name }} — devices</span>
          <div class="d-flex align-center ga-3">
            <v-select
              v-model="usagePeriod"
              :items="periods"
              label="Period"
              variant="outlined"
              density="compact"
              hide-details
              style="min-width: 13rem"
              data-testid="usage-period-select"
            ></v-select>
            <v-btn
              variant="text"
              :disabled="!usage.devices.length"
              @click="exportUsageCsv"
              data-testid="usage-export-btn"
            >
              <v-icon size="small" class="mr-1">fas fa-download</v-icon>
              CSV
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            <strong>{{ usage.active_device_count }}</strong> billable
            {{ usage.active_device_count === 1 ? 'seat' : 'seats' }} — a device active on any
            single day of the period counts as one.
          </p>
          <v-data-table
            :headers="usageHeaders"
            :items="usage.devices"
            :loading="loadingUsage"
            data-testid="usage-table"
          >
            <template #[`item.device_name`]="{ item }">{{ item.device_name || '—' }}</template>
            <template #[`item.device_serial`]="{ item }">
              <span class="mono">{{ item.device_serial || '—' }}</span>
            </template>
            <template #[`item.first_seen`]="{ item }">{{ formatDate(item.first_seen) }}</template>
            <template #[`item.last_seen`]="{ item }">{{ formatDate(item.last_seen) }}</template>
            <template #no-data>No devices activated in this period.</template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="usageDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="640" persistent>
      <v-card>
        <v-card-title>{{ editing ? 'Edit Capture Account' : 'New Capture Account' }}</v-card-title>
        <v-card-text>
          <v-form ref="accountForm" @submit.prevent="submit">
            <v-text-field
              v-model="form.name"
              label="Account Name"
              :rules="[required]"
              variant="outlined"
              color="primary"
              data-testid="name-input"
            ></v-text-field>

            <v-text-field
              v-model="form.organization_id"
              label="Apple Business Manager Organization ID"
              hint="The customer supplies this. Devices send it on every activation, so a typo blocks the whole fleet."
              persistent-hint
              :rules="[required]"
              variant="outlined"
              color="primary"
              class="mono-input"
              data-testid="org-id-input"
            ></v-text-field>

            <!-- Picked by email, never typed: a mistyped auth0_id links the account to
                 nobody, and there is no delete to undo it with. -->
            <v-autocomplete
              v-model="form.auth0_id"
              :items="userOptions"
              :loading="loadingUsers"
              item-title="title"
              item-value="value"
              label="Console user who will see the usage view"
              hint="Optional — leave empty to provision before the customer signs up."
              persistent-hint
              clearable
              variant="outlined"
              color="primary"
              class="mt-4"
              data-testid="user-select"
            ></v-autocomplete>

            <v-row class="mt-2">
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.license_ttl_days"
                  label="Licence TTL (days)"
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  :rules="[required, ttlRange]"
                  variant="outlined"
                  color="primary"
                  data-testid="ttl-input"
                ></v-text-field>
              </v-col>
              <v-col cols="6" v-if="editing">
                <v-select
                  v-model="form.status"
                  :items="STATUSES"
                  label="Status"
                  variant="outlined"
                  color="primary"
                  data-testid="status-select"
                ></v-select>
              </v-col>
            </v-row>

            <v-alert v-if="form.status === 'Terminated'" type="warning" variant="tonal" class="mb-4">
              Terminated is the end state — there is no delete. Devices get a hard 403 and
              erase their cached licence.
            </v-alert>

            <v-textarea
              v-model="form.internal_notes"
              label="Internal Notes"
              rows="2"
              variant="outlined"
              color="primary"
              data-testid="notes-input"
            ></v-textarea>

            <v-alert v-if="formError" type="error" variant="tonal" data-testid="form-error">
              {{ formError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-btn class="str-btn-primary" :loading="saving" @click="submit" data-testid="submit-btn">
            {{ editing ? 'Save' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="2000">{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { reasonFor, useCBERequest } from '@/composables';
import { downloadCsv, SEAT_CSV_COLUMNS } from '@/utils/csv';
import { currentMonth, monthOptions } from '@/utils/months';

const { makeCBERequest } = useCBERequest();

const STATUSES = ['Active', 'Suspended', 'Terminated'];
const ACCOUNTS_URL = '/piccolo/admin/capture/accounts';

const periods = monthOptions();

const accounts = ref([]);
const users = ref([]);
const loading = ref(false);
const loadingUsers = ref(false);
const search = ref('');

const usageDialog = ref(false);
const usageAccount = ref(null);
const usagePeriod = ref(currentMonth());
const usage = ref({ active_device_count: 0, devices: [] });
const loadingUsage = ref(false);

const dialog = ref(false);
const editing = ref(null);
const accountForm = ref(null);
const formError = ref('');
const saving = ref(false);
const snackbar = ref(false);
const snackbarText = ref('');

const form = reactive({
  name: '',
  organization_id: '',
  auth0_id: null,
  license_ttl_days: 30,
  status: 'Active',
  internal_notes: '',
});

const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Organization ID', key: 'organization_id' },
  { title: 'Linked User', key: 'auth0_id' },
  { title: 'Status', key: 'status' },
  { title: 'Active Seats', key: 'active_device_count' },
  { title: 'TTL', key: 'license_ttl_days' },
  { title: 'Created', key: 'created_on' },
  { title: '', key: 'actions', sortable: false },
];

const usageHeaders = [
  { title: 'Device Name', key: 'device_name' },
  { title: 'Serial Number', key: 'device_serial' },
  { title: 'Model', key: 'device_model' },
  { title: 'App Version', key: 'app_version' },
  { title: 'First Seen', key: 'first_seen' },
  { title: 'Last Active', key: 'last_seen' },
];

const required = value => (value !== null && value !== undefined && value !== '') || 'Required.';
// Integer as well as range: the column is an `int`, so a fractional TTL is a 422 from
// pydantic rather than something the range check would have caught.
const ttlRange = value =>
  (Number.isInteger(value) && value >= 1 && value <= 365) ||
  'Must be a whole number between 1 and 365.';

// UTC dates, matching the window that counted them — see CaptureSeats.vue.
const formatDate = (value) =>
  (value ? new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' }) : '—');

const statusColour = (status) =>
  ({ Active: 'green', Suspended: 'orange', Terminated: 'red' }[status] || 'grey');

// `period_end` is exclusive, so name the month rather than print a range that looks like
// it runs a day past the period. UTC, or a viewer west of Greenwich reads the window's
// UTC-midnight start as the previous month and sees Aug against a September count.
const periodLabel = (account) =>
  account.period_start
    ? new Date(account.period_start).toLocaleDateString(undefined, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    })
    : '—';

const userOptions = computed(() =>
  users.value
    .filter(user => user.auth0_id)
    .map(user => ({
      title: user.organization ? `${user.email} — ${user.organization}` : user.email,
      value: user.auth0_id,
    }))
);

const emailFor = (auth0Id) => users.value.find(user => user.auth0_id === auth0Id)?.email;

const fetchAccounts = async () => {
  loading.value = true;
  try {
    accounts.value = await makeCBERequest(ACCOUNTS_URL, 'get') ?? [];
  } finally {
    loading.value = false;
  }
};

const fetchUsers = async () => {
  loadingUsers.value = true;
  try {
    users.value = await makeCBERequest('/piccolo/admin/users', 'get') ?? [];
  } finally {
    loadingUsers.value = false;
  }
};

const fetchUsage = async () => {
  if (!usageAccount.value) return;
  loadingUsage.value = true;
  try {
    const response = await makeCBERequest(
      `${ACCOUNTS_URL}/${usageAccount.value.id}/usage`,
      'get',
      {},
      { period: usagePeriod.value }
    );
    if (response) usage.value = response;
  } finally {
    loadingUsage.value = false;
  }
};

const openUsage = (account) => {
  usageAccount.value = account;
  // Open on the month whose count was clicked, not on whatever was last inspected.
  usagePeriod.value = account.period_start
    ? account.period_start.slice(0, 7)
    : currentMonth();
  usage.value = { active_device_count: 0, devices: [] };
  usageDialog.value = true;
};

const exportUsageCsv = () => {
  downloadCsv(
    `capture-seats-${usageAccount.value.organization_id}-${usagePeriod.value}.csv`,
    SEAT_CSV_COLUMNS,
    usage.value.devices
  );
};

// Opening the dialog and changing the month are the only two things that need a fetch,
// and Vue coalesces both refs changing in one tick into a single callback — so this
// cannot fire the duplicate request that an explicit call alongside a watcher did.
// Watching the dialog rather than the account also refetches when the same account is
// reopened, which `usage` having been cleared requires.
watch([usageDialog, usagePeriod], () => {
  if (usageDialog.value) fetchUsage();
});

const openCreate = () => {
  editing.value = null;
  Object.assign(form, {
    name: '',
    organization_id: '',
    auth0_id: null,
    license_ttl_days: 30,
    status: 'Active',
    internal_notes: '',
  });
  formError.value = '';
  dialog.value = true;
};

const openEdit = (account) => {
  editing.value = account;
  Object.assign(form, {
    name: account.name,
    organization_id: account.organization_id,
    auth0_id: account.auth0_id,
    license_ttl_days: account.license_ttl_days,
    status: account.status,
    internal_notes: account.internal_notes || '',
  });
  formError.value = '';
  dialog.value = true;
};

const submit = async () => {
  formError.value = '';
  const { valid } = await accountForm.value.validate();
  if (!valid) return;

  saving.value = true;
  try {
    if (editing.value) {
      await makeCBERequest(
        `${ACCOUNTS_URL}/${editing.value.id}`,
        'patch',
        { ...form, auth0_id: form.auth0_id || null },
        {},
        { rethrow: true }
      );
    } else {
      // `status` is not accepted on create (`extra="forbid"` makes it a 422), and a new
      // account is Active by default anyway.
      await makeCBERequest(
        ACCOUNTS_URL,
        'post',
        {
          name: form.name,
          organization_id: form.organization_id,
          auth0_id: form.auth0_id || undefined,
          license_ttl_days: form.license_ttl_days,
          internal_notes: form.internal_notes,
        },
        {},
        { rethrow: true }
      );
    }
    dialog.value = false;
    await fetchAccounts();
    snackbarText.value = editing.value ? 'Account updated.' : 'Account created.';
    snackbar.value = true;
  } catch (error) {
    formError.value = reasonFor(error);
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchAccounts();
  fetchUsers();
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/StrVariables.scss'
@import '@/assets/styles/StrButtons.scss'

.mono, .mono-input :deep(input)
  font-family: monospace

.period
  font-size: 0.8rem
  opacity: 0.7
</style>
